import { SuccessModalData } from '../components/success-modal/success-modal.component';
import { PhoneValidator } from '../validators/phone.validator';

/** Standard copy for all payment-completed success modals (not payment-link waiting UI). */
export const PAYMENT_SUCCESS_MODAL_DEFAULTS: Readonly<SuccessModalData> = {
  title: 'Payment Successful!',
  description:
    'The order has been successfully completed. The customer may now proceed to the car wash.',
  buttonText: 'Close',
};

export interface PaymentSuccessOrderFields {
  packageName?: string;
  customerName?: string;
  vehicleYear?: string;
  vehicleMake?: string;
  vehicleModel?: string;
  /** When set, appended to vehicle model (e.g. LPR checkout). */
  licensePlate?: string;
}

export function isEmptyOrUnknown(value: string | undefined | null): boolean {
  if (value == null) {
    return true;
  }
  const trimmed = String(value).trim();
  return (
    trimmed === '' ||
    trimmed.toLowerCase() === 'any' ||
    trimmed === 'N/A' ||
    trimmed.toLowerCase() === 'customer'
  );
}

/** True when the value is only a phone number (must not be shown as customer name). */
export function looksLikePhoneNumber(value: string | undefined | null): boolean {
  const trimmed = String(value ?? '').trim();
  if (!trimmed) {
    return false;
  }
  return PhoneValidator.isValidUsaPhone(trimmed);
}

/**
 * Resolve a displayable customer name from name parts only — never phone/contact fields.
 */
export function resolveRawCustomerName(input: {
  firstName?: string | null;
  lastName?: string | null;
  customerName?: string | null;
  fallback?: string | null;
}): string {
  const first = String(input.firstName ?? '').trim();
  const last = String(input.lastName ?? '').trim();
  const joined = [first, last].filter(Boolean).join(' ').trim();

  const explicit = String(input.customerName ?? '').trim();
  const fromExplicit =
    explicit && !looksLikePhoneNumber(explicit) ? explicit : '';

  const fb = String(input.fallback ?? '').trim();
  const fromFallback = fb && !looksLikePhoneNumber(fb) ? fb : '';

  return joined || fromExplicit || fromFallback;
}

/** Display label for success modal when no real customer name exists. */
export function formatCustomerNameForDisplay(raw?: string | null): string {
  const name = resolveRawCustomerName({
    customerName: raw,
  });
  if (isEmptyOrUnknown(name)) {
    return 'N/A';
  }
  return name;
}

export function normalizePackageNameForDisplay(raw?: string | null): string {
  if (!raw) {
    return '';
  }
  return String(raw)
    .replace(/^Car\s+Wash\s+Package\s+/i, '')
    .replace(/\s*(Single\s+Wash|Monthly\s+Membership|Single|Monthly|Membership)\s*$/i, '')
    .trim();
}

export function formatVehicleModelWithPlate(model: string, licensePlate?: string): string {
  const m = String(model ?? '').trim();
  const plate = String(licensePlate ?? '').trim();
  if (!plate) {
    return m;
  }
  return m ? `${m} · ${plate}` : plate;
}

export function formatPaymentSuccessVehicleDisplay(fields: PaymentSuccessOrderFields): string {
  const parts: string[] = [];
  const year = fields.vehicleYear?.trim();
  if (year && year !== '0' && !isEmptyOrUnknown(year)) {
    parts.push(year);
  }
  if (fields.vehicleMake && !isEmptyOrUnknown(fields.vehicleMake)) {
    parts.push(fields.vehicleMake.trim());
  }
  const model = formatVehicleModelWithPlate(fields.vehicleModel ?? '', fields.licensePlate);
  if (model && !isEmptyOrUnknown(model)) {
    parts.push(model);
  }
  return parts.length > 0 ? parts.join(' ') : '';
}

export function buildPaymentSuccessModalData(
  fields: PaymentSuccessOrderFields,
  overrides?: Partial<SuccessModalData>
): SuccessModalData {
  return {
    ...PAYMENT_SUCCESS_MODAL_DEFAULTS,
    packageName: fields.packageName ?? '',
    customerName: formatCustomerNameForDisplay(fields.customerName),
    vehicleYear: fields.vehicleYear ?? '',
    vehicleMake: fields.vehicleMake ?? '',
    vehicleModel: formatVehicleModelWithPlate(fields.vehicleModel ?? '', fields.licensePlate),
    ...overrides,
  };
}

function orderFieldString(o: Record<string, any>, ...keys: string[]): string | undefined {
  for (const key of keys) {
    const v = o[key];
    if (v != null && String(v).trim() !== '') {
      return String(v);
    }
  }
  return undefined;
}

export function extractPaymentSuccessFieldsFromApiOrder(
  apiOrder: Record<string, any> | null | undefined,
  fallbacks: PaymentSuccessOrderFields = {}
): PaymentSuccessOrderFields {
  const o = apiOrder ?? {};

  return {
    packageName:
      normalizePackageNameForDisplay(String(o['package_name'] ?? o['packageName'] ?? '')) ||
      fallbacks.packageName ||
      '',
    customerName: formatCustomerNameForDisplay(
      resolveRawCustomerName({
        firstName: orderFieldString(o, 'customer_first_name', 'customerFirstName'),
        lastName: orderFieldString(o, 'customer_last_name', 'customerLastName'),
        customerName: orderFieldString(o, 'customer_name', 'customerName'),
        fallback: fallbacks.customerName,
      })
    ),
    vehicleYear:
      String(o['year'] ?? o['vehicle_year'] ?? o['vehicleYear'] ?? fallbacks.vehicleYear ?? '').trim(),
    vehicleMake: String(o['make'] ?? o['vehicle_make'] ?? o['vehicleMake'] ?? fallbacks.vehicleMake ?? '').trim(),
    vehicleModel: String(o['model'] ?? o['vehicle_model'] ?? o['vehicleModel'] ?? fallbacks.vehicleModel ?? '').trim(),
    licensePlate: fallbacks.licensePlate,
  };
}
